import * as z from "zod";

const ANALYTICS_REPORT_PLAN_TOOL_NAME = "analytics-report-plan";

// Threads saved under either the custom or the analytics tool names show their cards.
const PLAN_REPORT_TOOL_NAMES: ReadonlySet<string> = new Set([
  ANALYTICS_REPORT_PLAN_TOOL_NAME,
  "create-custom-analytics-report",
  "plan-custom-analytics-report",
]);
const PLAN_REPORT_EDIT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "analytics-report-edit-plan",
  "plan-custom-analytics-report-edit",
]);
const SAVE_REPORT_TOOL_NAMES: ReadonlySet<string> = new Set([
  ANALYTICS_REPORT_PLAN_TOOL_NAME,
  "analytics-report-create-from-plan",
  "create-custom-analytics-report",
  "analytics-report-edit-from-plan",
  "edit-custom-analytics-report",
]);

const CustomAnalyticsReportPlanSectionSchema = z.object({
  title: z.string().min(1),
  values: z.array(z.string().min(1)),
});

const CustomAnalyticsReportPlanSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  sections: z.array(CustomAnalyticsReportPlanSectionSchema),
});

const ReadyForConfirmationOutputSchema = z
  .object({
    status: z.literal("ready_for_confirmation"),
    reportPlan: z.string().optional(),
    reportPlanCard: CustomAnalyticsReportPlanSchema.optional(),
    customReportTitle: z.string().optional(),
  })
  .passthrough();

const CreatedReportOutputSchema = z
  .object({
    status: z.enum(["preparing", "ready"]),
    customReportUrl: z.string().min(1),
  })
  .passthrough();

export type CustomAnalyticsReportAction =
  | {
      type: "confirm";
      operation: "create" | "edit";
      label: string;
      message: string;
      plan: CustomAnalyticsReportPlan | null;
      reportPlan: string | null;
    }
  | { type: "view-report"; url: string };

export interface CustomAnalyticsReportPlan {
  title: string;
  description: string;
  sections: readonly CustomAnalyticsReportPlanSection[];
}

export interface CustomAnalyticsReportPlanSection {
  title: string;
  values: readonly string[];
}

export function tryParseCustomAnalyticsReportAction(
  toolName: string,
  output: unknown,
): CustomAnalyticsReportAction | null {
  if (toolName === "execute_code") {
    const result = z
      .object({ customAnalyticsReport: z.unknown() })
      .safeParse(output);
    return result.success
      ? tryParseCustomAnalyticsReportAction(
          ANALYTICS_REPORT_PLAN_TOOL_NAME,
          result.data.customAnalyticsReport,
        )
      : null;
  }
  const readyForConfirmationOutput =
    ReadyForConfirmationOutputSchema.safeParse(output);
  if (
    PLAN_REPORT_TOOL_NAMES.has(toolName) &&
    readyForConfirmationOutput.success
  ) {
    const output = readyForConfirmationOutput.data;
    const reportPlan = output.reportPlan ?? null;
    return {
      type: "confirm",
      operation: "create",
      label: "Generate report",
      message: "Yes, create it.",
      plan: output.reportPlanCard ?? null,
      reportPlan,
    };
  }

  if (
    PLAN_REPORT_EDIT_TOOL_NAMES.has(toolName) &&
    readyForConfirmationOutput.success
  ) {
    const output = readyForConfirmationOutput.data;
    const reportPlan = output.reportPlan ?? null;
    return {
      type: "confirm",
      operation: "edit",
      label: "Update report",
      message: "Yes, update it.",
      plan: output.reportPlanCard ?? null,
      reportPlan,
    };
  }

  if (SAVE_REPORT_TOOL_NAMES.has(toolName)) {
    const result = CreatedReportOutputSchema.safeParse(output);
    if (result.success) {
      return { type: "view-report", url: result.data.customReportUrl };
    }
  }

  return null;
}

export function confirmationPromptForCustomAnalyticsReportAction(
  action: Extract<CustomAnalyticsReportAction, { type: "confirm" }>,
): string {
  return action.operation === "edit"
    ? "Does this look right, and should I update it?"
    : "Does this look right, and should I create it?";
}
